import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Grid, makeStyles} from '@material-ui/core';
import DialogContainer, {DialogButton} from '../../../../components/AlertDialog/DialogContainer';
import {TransactionItem} from '../../../../store/actions/accounts/payloads';
import {getAdditionalInfo} from './TransactionsTab.utils';
import {ReduxState} from '../../../../store/types';
import dayjs from '../../../../services/customDayJs';
import {convertFormatFromBackendToDayJs} from '../../../../utils/dateWithTimezoneConversion';
import {Colors} from '../../../../styles/Colors';
import CustomizedTextField from '../../../../components/TextField/TextField';

const useDialogStyles = makeStyles(() => ({
    modalContainer: {
        '& .MuiDialog-paper': {
            maxWidth: 965,
            minWidth: 965,
        },
        '& .MuiDialogContent-root': {
            background: Colors.SmokeBackground,
            padding: '36px 130px',
            paddingBottom:240,
            overflowX: 'hidden',
        },
    },
    header: {
        '& .MuiTypography-h6': {
            fontWeight: 'bold',
            fontSize: 18,
        },
    },
    primaryActionButton: {
        '& .MuiButton-label': {
            color: Colors.Primary,
        },
    },
    inputRow: {
        display: 'flex',
        marginTop: 22,
        '& .MuiFormControl-root': {
            flex: 1,
            margin: '0 6px',
        },
    },
}));

interface TransactionDetailsDialogProps {
    selectedTransaction: TransactionItem | null;
    onClose: () => void;
}

const TransactionDetailsDialog: React.VFC<TransactionDetailsDialogProps> = ({
    selectedTransaction,
    onClose,
}) => {
    const {t} = useTranslation();
    const dialogClasses = useDialogStyles();

    const userDateFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.out_date_format || '',
    );
    const customerCurrency = useSelector<ReduxState, string>(
        (state) =>
            state.calls.customerCurrency
            || state.generic.globalCustomerInfo?.customer_info?.iso_4217
            || '',
    );

    const formatDate = (date?: string) => {
        if (!date) return '—';
        return dayjs(date).format(
            userDateFormat
                ? convertFormatFromBackendToDayJs(userDateFormat) + ' HH:mm:ss'
                : 'DD.MM.YYYY HH:mm:ss',
        );
    };

    return (
        <DialogContainer
            isOpen={!!selectedTransaction}
            header={t('screens:accounts.transactionDetailsTitle')}
            headerClass={dialogClasses.header}
            className={dialogClasses.modalContainer}
            onClose={onClose}
            dialogActionsButtons={[
                <DialogButton
                    className={dialogClasses.primaryActionButton}
                    key="close"
                    label={t('common:close')}
                    onClick={onClose}
                />,
            ]}
        >
            {selectedTransaction && (
                <>
                    <Grid className={dialogClasses.inputRow}>
                        <CustomizedTextField
                            id="connectTime"
                            label={t('screens:accounts.connectTime')}
                            value={formatDate(selectedTransaction.connect_time)}
                            readOnly
                        />
                        <CustomizedTextField
                            id="disconnectTime"
                            label={t('screens:accounts.disconnectTime')}
                            value={formatDate(selectedTransaction.disconnect_time)}
                            readOnly
                        />
                    </Grid>
                    <Grid className={dialogClasses.inputRow}>
                        <CustomizedTextField
                            id="service"
                            label={t('screens:accounts.serviceDetails')}
                            value={selectedTransaction.service ?? '—'}
                            readOnly
                        />
                        <CustomizedTextField
                            id="additionalInfo"
                            label={t('screens:accounts.additionalInfo')}
                            value={getAdditionalInfo(selectedTransaction)}
                            readOnly
                        />
                    </Grid>
                    <Grid className={dialogClasses.inputRow}>
                        <CustomizedTextField
                            id="description"
                            label={t('screens:accounts.description')}
                            value={selectedTransaction.description ?? '—'}
                            readOnly
                        />
                        <CustomizedTextField
                            id="charge"
                            label={`${t('screens:calls.charge')}${customerCurrency ? `, ${customerCurrency}` : ''}`}
                            value={selectedTransaction.charged_amount !== undefined
                                ? selectedTransaction.charged_amount.toFixed(2)
                                : '—'}
                            readOnly
                        />
                    </Grid>
                </>
            )}
        </DialogContainer>
    );
};

export default TransactionDetailsDialog;